/* eslint-disable no-useless-escape */
import { zodResolver } from "@hookform/resolvers/zod";
import { CognitoUserAttribute } from "amazon-cognito-identity-js";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CheckIcon } from "lucide-react";
import posthog from "posthog-js";
import { useContext, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import GoogleSignInButton from "../../../components/auth/GoogleSignInButton";
import ErrorToast from "../../../components/ErrorToast";
import PasswordInput from "../../../components/PasswordInput";
import Spinner from "../../../components/Spinner";
import { Button } from "../../../components/ui/button";
import { Field, FieldError, FieldLabel } from "../../../components/ui/field";
import { Input } from "../../../components/ui/input";
import ResumeContext from "../../../context/ResumeContext";
import { isValidEmail, isValidPassword, isValidPhoneNumber } from "../../../handlers/validationFunctions";
import { useGoogleSignIn } from "../../../hooks/useGoogleSignIn";
import { cn } from "../../../lib/utils";
import { userPool } from "../../../services/auth/cognito";
import { redirectAfterSignIn } from "../../../services/auth/postAuthRedirect";
import { setPendingSignup } from "../pendingSignup";
import LinkedInLogin from "../SigninScreen/LinkedInLogin";

const referer = new URLSearchParams(window.location.search).get("ref");

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

const INVALID_PASSWORD_MESSAGE =
	"<ul><li>Password must have least 1 number</li><li>Password must have least 1 special character(^ $ *)</li><li>Password must have at least 1 uppercase letter</li><li>Password must have at least 1 lowercase letter</li></ul>";

const registrationSchema = z
	.object({
		firstName: z.string(),
		lastName: z.string(),
		email: z.string(),
		phone: z.string(),
		password: z.string(),
		confirm_password: z.string(),
	})
	.superRefine((values, context) => {
		const missingFields = [
			["firstName", values.firstName, "First name is required"],
			["lastName", values.lastName, "Last name is required"],
			["email", values.email, "Email is required"],
			["password", values.password, "Password required"],
			["confirm_password", values.confirm_password, "Confirm password is required"],
		] as const;

		if (missingFields.some(([, value]) => !value)) {
			for (const [field, value, message] of missingFields) {
				if (!value) {
					context.addIssue({ code: z.ZodIssueCode.custom, message, path: [field] });
				}
			}
			return;
		}

		if (!isValidEmail(values.email)) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Please enter a valid email address",
				path: ["email"],
			});
			return;
		}

		if (values.phone && !isValidPhoneNumber(values.phone)) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Please enter a valid phone number, eg. +2557xxxxxxxx",
				path: ["phone"],
			});
			return;
		}

		if (!isValidPassword(values.password)) {
			context.addIssue({ code: z.ZodIssueCode.custom, message: INVALID_PASSWORD_MESSAGE, path: ["password"] });
			return;
		}

		if (values.password !== values.confirm_password) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Password and confirm password should match",
				path: ["confirm_password"],
			});
		}
	});

type RegistrationFormValues = z.infer<typeof registrationSchema>;

function JobSeekerRegistrationForm() {
	const {
		control,
		formState: { errors },
		getValues,
		handleSubmit: submitForm,
		register,
		reset,
	} = useForm<RegistrationFormValues>({
		resolver: zodResolver(registrationSchema),
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
			phone: "",
			password: "",
			confirm_password: "",
		},
	});

	const [showEmailOptions, setShowEmailOptions] = useState(false);

	const navigate = useNavigate();
	const [serverError, setServerError] = useState("");
	const [loading, setLoading] = useState(false);
	const password = useWatch({ control, name: "password" });
	const confirmPassword = useWatch({ control, name: "confirm_password" });
	const reduceMotion = useReducedMotion();

	const resumeContext = useContext(ResumeContext);

	useEffect(() => {
		const contactInfo = resumeContext?.resumeData?.contactInfo;
		if (!contactInfo) return;

		const currentValues = getValues();
		reset(
			{
				...currentValues,
				firstName: currentValues.firstName || contactInfo.firstName || "",
				lastName: currentValues.lastName || contactInfo.lastName || "",
				email: currentValues.email || contactInfo.email || "",
				phone: currentValues.phone || contactInfo.phoneNo || "",
			},
			{ keepDirty: true, keepErrors: true, keepTouched: true },
		);
	}, [getValues, reset, resumeContext]);

	function trackSignupFailed(reason: string) {
		posthog.capture("registration_signup_failed", { reason, method: "email" });
	}

	function handleSubmit({ firstName, lastName, email, phone, password }: RegistrationFormValues) {
		setServerError("");
		setLoading(true);

		posthog.capture("registration_signup_attempted", { method: "email" });

		const attributeList = [new CognitoUserAttribute({ Name: "email", Value: email })];

		userPool.signUp(email, password, attributeList, null, (err) => {
			if (err) {
				const cognitoError = err as { code?: string; message?: string };
				if (cognitoError.code === "UsernameExistsException") {
					setServerError("Email already exists");
					trackSignupFailed("email_exists");
				} else {
					setServerError(cognitoError.message || "We couldn't create your account. Please try again.");
					trackSignupFailed("cognito_error");
				}
			} else {
				posthog.capture("registration_account_created", { method: "email" });

				setPendingSignup({ firstName, lastName, email, phone, method: "email" });

				const goTo = referer && referer.includes("expert-help") ? "/signin?ref=" + referer : "/onboarding";
				navigate(goTo, { state: { method: "email" } });
			}
			setLoading(false);
		});
	}

	function handleInvalidSubmit() {
		const { firstName, lastName, email, phone, password, confirm_password } = getValues();

		setServerError("");
		setLoading(false);
		posthog.capture("registration_signup_attempted", { method: "email" });

		if (!firstName || !lastName || email === "" || password === "" || confirm_password === "") {
			trackSignupFailed("missing_fields");
		} else if (!isValidEmail(email)) {
			trackSignupFailed("invalid_email");
		} else if (phone && !isValidPhoneNumber(phone)) {
			trackSignupFailed("invalid_phone");
		} else if (!isValidPassword(password)) {
			trackSignupFailed("invalid_password");
		} else if (password !== confirm_password) {
			trackSignupFailed("password_mismatch");
		}
	}

	const passwordRequirements = [
		{ label: "8 or more characters", met: password.length >= 8 },
		{ label: "A number", met: /\d/.test(password) },
		{ label: "A special character", met: /[!@#$%^&*()_+\-=\[\]/{};&#39;:"\\|,.<>\/?]/.test(password) },
		{ label: "An uppercase letter", met: /[A-Z]/.test(password) },
	];
	const requirementsMet = passwordRequirements.filter((requirement) => requirement.met).length;
	const passwordComplete = requirementsMet === passwordRequirements.length;
	const passwordsMatch = password.length > 0 && password === confirmPassword;
	const passwordSettled = passwordComplete && passwordsMatch;

	const strengthLabel = ["Keep going", "Keep going", "Getting there", "Almost there", "Strong password"][
		requirementsMet
	];
	const strengthColor = ["bg-gray-200", "bg-error-default", "bg-orange-500", "bg-amber-500", "bg-success-default"][
		requirementsMet
	];

	const { handleGoogleSignIn, handleGoogleError } = useGoogleSignIn({
		accountCheckAttempts: 2,
		onSignedIn: () => {
			if (referer?.includes("expert-help")) {
				setLoading(false);
				navigate("/expert-help/personal-info");
				return "stop";
			}
			setLoading(true);
		},
		onExistingAccount: (session) => {
			setLoading(false);
			posthog.capture("registration_existing_account_signed_in", { method: "google" });
			redirectAfterSignIn(session.credential, (destination) => navigate(destination, { replace: true }));
		},
		onNewAccount: (session) => {
			setLoading(false);
			posthog.capture("registration_account_created", { method: "google" });
			session.publish();
			navigate("/onboarding", { state: { method: "google" } });
		},
		onAccountCheckFailed: () => {
			setLoading(false);
			posthog.capture("registration_account_check_failed", { method: "google" });
			toast.error("We couldn't verify your account. Please try again.");
		},
	});

	return (
		<>
			<div className="flex flex-col gap-3">
				<LinkedInLogin />
				<GoogleSignInButton onSuccess={handleGoogleSignIn} onError={handleGoogleError} oneTap />
			</div>
			<div className="my-5 flex items-center gap-3" aria-hidden="true">
				<span className="h-px flex-1 bg-gray-200" />
				<span className="text-xs font-medium uppercase tracking-wide text-gray-400">or</span>
				<span className="h-px flex-1 bg-gray-200" />
			</div>
			{serverError !== "" && <ErrorToast error={serverError} />}{" "}
			<AnimatePresence initial={false} mode="wait">
				{showEmailOptions ? (
					<motion.div
						key="email-form"
						initial={reduceMotion ? false : { height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
						transition={reduceMotion ? { duration: 0 } : { duration: 0.35, ease: EASE }}
						className="overflow-hidden"
					>
						<form noValidate onSubmit={submitForm(handleSubmit, handleInvalidSubmit)} className="tr-form">
							<div className="grid grid-cols-2 gap-3">
								<Field className="gap-1 mb-4" data-invalid={!!errors.firstName}>
									<FieldLabel htmlFor="registration-first-name">First name</FieldLabel>
									<Input
										id="registration-first-name"
										type="text"
										placeholder="Your first name"
										aria-invalid={!!errors.firstName}
										aria-describedby={errors.firstName ? "registration-first-name-error" : undefined}
										{...register("firstName")}
									/>
									<FieldError
										id="registration-first-name-error"
										className="text-error-default"
										errors={[errors.firstName]}
									/>
								</Field>
								<Field className="gap-1 mb-4" data-invalid={!!errors.lastName}>
									<FieldLabel htmlFor="registration-last-name">Last name</FieldLabel>
									<Input
										id="registration-last-name"
										type="text"
										placeholder="Your last name"
										aria-invalid={!!errors.lastName}
										aria-describedby={errors.lastName ? "registration-last-name-error" : undefined}
										{...register("lastName")}
									/>
									<FieldError
										id="registration-last-name-error"
										className="text-error-default"
										errors={[errors.lastName]}
									/>
								</Field>
							</div>
							<Field className="gap-1 mb-4" data-invalid={!!errors.email}>
								<FieldLabel htmlFor="registration-email">Email</FieldLabel>
								<Input
									id="registration-email"
									type="email"
									placeholder="Enter your email address"
									aria-invalid={!!errors.email}
									aria-describedby={errors.email ? "registration-email-error" : undefined}
									{...register("email")}
								/>
								<FieldError id="registration-email-error" className="text-error-default" errors={[errors.email]} />
							</Field>
							<Field className="gap-1 mb-4" data-invalid={!!errors.phone}>
								<FieldLabel htmlFor="registration-phone">Phone number</FieldLabel>
								<Input
									id="registration-phone"
									type="tel"
									placeholder="Phone number (optional)"
									aria-invalid={!!errors.phone}
									aria-describedby={errors.phone ? "registration-phone-error" : undefined}
									{...register("phone")}
								/>
								<FieldError id="registration-phone-error" className="text-error-default" errors={[errors.phone]} />
							</Field>
							<Field className="gap-1 mb-4" data-invalid={!!errors.password}>
								<FieldLabel htmlFor="registration-password">Password</FieldLabel>
								<PasswordInput
									id="registration-password"
									placeholder="Create a password"
									aria-invalid={!!errors.password}
									aria-describedby={errors.password ? "registration-password-error" : undefined}
									{...register("password")}
								/>
								<FieldError
									id="registration-password-error"
									className="text-error-default"
									errors={[errors.password]}
								/>
							</Field>
							<AnimatePresence initial={false}>
								{password.length > 0 && (
									<motion.div
										key="password-feedback"
										initial={reduceMotion ? false : { height: 0, opacity: 0 }}
										animate={{ height: "auto", opacity: 1 }}
										exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
										transition={reduceMotion ? { duration: 0 } : { duration: 0.35, ease: EASE }}
										className="overflow-hidden"
									>
										<div
											className="mb-4 grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
											style={{ gridTemplateRows: passwordSettled ? "0fr" : "1fr" }}
										>
											<div className="overflow-hidden">
												<div
													aria-label="Password strength"
													aria-valuemax={passwordRequirements.length}
													aria-valuemin={0}
													aria-valuenow={requirementsMet}
													className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
													role="progressbar"
												>
													<div
														className={cn(
															"h-full w-full origin-left rounded-full transition-transform duration-500 ease-out motion-reduce:transition-none",
															strengthColor,
														)}
														style={{ transform: `scaleX(${requirementsMet / passwordRequirements.length})` }}
													/>
												</div>
												<p aria-live="polite" className="mt-2 text-sm font-medium text-gray-800">
													{strengthLabel}
												</p>
												<ul className="mt-2 flex flex-col gap-1.5">
													{passwordRequirements.map((requirement) => (
														<li className="flex items-center gap-2 text-sm" key={requirement.label}>
															{requirement.met ? (
																<CheckIcon aria-hidden="true" className="size-4 shrink-0 text-success-default" />
															) : (
																<span
																	aria-hidden="true"
																	className={cn(
																		"size-4 shrink-0 rounded-full border",
																		errors.password ? "border-error-default" : "border-gray-300",
																	)}
																/>
															)}
															<span
																className={cn(
																	requirement.met
																		? "text-gray-700"
																		: errors.password
																			? "text-error-default"
																			: "text-gray-500",
																)}
															>
																{requirement.label}
															</span>
															<span className="sr-only">{requirement.met ? " — done" : " — still needed"}</span>
														</li>
													))}
												</ul>
											</div>
										</div>
										<Field className="gap-1 mb-4" data-invalid={!!errors.confirm_password}>
											<FieldLabel htmlFor="registration-confirm-password">Confirm password</FieldLabel>
											<PasswordInput
												id="registration-confirm-password"
												placeholder="Confirm Password"
												aria-invalid={!!errors.confirm_password}
												aria-describedby={errors.confirm_password ? "registration-confirm-password-error" : undefined}
												{...register("confirm_password")}
											/>
											<FieldError
												id="registration-confirm-password-error"
												className="text-error-default"
												errors={[errors.confirm_password]}
											/>
											<AnimatePresence initial={false} mode="wait">
												{confirmPassword.length > 0 && !errors.confirm_password ? (
													<motion.p
														key={passwordsMatch ? "match" : "no-match"}
														aria-live="polite"
														initial={reduceMotion ? false : { opacity: 0, y: -3 }}
														animate={{ opacity: 1, y: 0 }}
														exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -3 }}
														transition={{ duration: 0.2, ease: EASE }}
														className={cn(
															"mt-1.5 flex items-center gap-2 text-sm",
															passwordsMatch ? "text-success-default" : "text-gray-500",
														)}
													>
														{passwordsMatch ? (
															<CheckIcon aria-hidden="true" className="size-4 shrink-0" />
														) : (
															<span
																aria-hidden="true"
																className="size-4 shrink-0 rounded-full border border-gray-300"
															/>
														)}
														{passwordsMatch ? "Passwords match" : "Passwords don't match yet"}
													</motion.p>
												) : null}
											</AnimatePresence>
										</Field>
									</motion.div>
								)}
							</AnimatePresence>
							<Button type="submit" className="w-full" disabled={loading}>
								{loading ? <Spinner /> : "Create account"}
							</Button>
						</form>
					</motion.div>
				) : (
					<motion.div
						key="email-button"
						initial={reduceMotion ? false : { opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={reduceMotion ? { duration: 0 } : { duration: 0.12, ease: EASE }}
					>
						<Button
							type="button"
							variant="outlineSecondary"
							className="w-full"
							onClick={() => {
								posthog.capture("registration_email_form_opened");
								setShowEmailOptions(true);
							}}
						>
							Continue with Email
						</Button>
					</motion.div>
				)}
			</AnimatePresence>
			<div className="mt-6 text-sm text-gray-600">
				<span>
					Already have an account?{" "}
					<Link to="/signin" className="font-semibold text-primary-700">
						Log in
					</Link>
				</span>
			</div>
		</>
	);
}

export default JobSeekerRegistrationForm;
